// src/pages/TrialBalance.jsx
import { useState, useRef, useEffect, Fragment } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { mapAccount, parseTrialBalance, getAFSJobs, getFirm, getOrCreateDraftJob, saveJobProgress, checkDuplicateAFSJob, aggregateFigures, checkTrialBalanceExclusion, validateAFS } from '../api/kailux'
import { isUnmappedAccount, isControlRow, isExcludedControlRow, findProfitRollupCodes, applyMappingEdit, statementForCategory, hasStatementConflict } from '../utils/accountMapping'
import { preflightIssues, preflightMessage } from '../utils/preflight'
import { FileText, PenLine, FolderOpen, Sparkles, BarChart3 } from 'lucide-react'
import BackButton from '../components/BackButton'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
// Phase 1 (Jul 2026): free financial year-end date-range picker helpers.
// Neither the Companies Act nor IFRS for SMEs restricts AFS preparation to a
// fixed 12-month period ending on a preset date — a firm may legitimately
// need a stub/interim period (mid-year engagement) or a historical period
// (years back, for a client that never had statements done). These helpers
// convert between <input type="date"> values and the "D Month YYYY" display
// string the backend/PDF engine expects, with no artificial floor on how
// far back a date can go and no restriction to full 12-month periods.
const MONTH_NAMES = ['January','February','March','April','May','June','July',
                      'August','September','October','November','December']

export function monthNameToIndex(name) {
  const i = MONTH_NAMES.findIndex(m => m.toLowerCase() === String(name || '').toLowerCase())
  return i >= 0 ? i : 1 // default to February (index 1) if unrecognised
}

export function isoToDisplayDate(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return ''
  return `${d} ${MONTH_NAMES[m - 1]} ${y}`
}

export function defaultPeriodDates(client, currentYear) {
  const monthIdx = monthNameToIndex(client?.year_end_month)
  const day      = client?.year_end_day || 28
  // Default end date: the client's usual year-end, in the current year —
  // still fully overridable via the picker below, this is just a sensible
  // starting point, not a restriction.
  const end = new Date(currentYear, monthIdx, day)
  const start = new Date(end)
  start.setFullYear(start.getFullYear() - 1)
  start.setDate(start.getDate() + 1)
  // Format from LOCAL date parts. Never use d.toISOString() here: `end` is
  // built at local midnight, and toISOString() converts to UTC, which in any
  // timezone ahead of UTC (South Africa is UTC+2) lands on the PREVIOUS day —
  // a 28 Feb year-end became 27 Feb and the start 28 Feb instead of 1 Mar
  // (seen on a real job, 29 Sep 2026).
  const pad = n => String(n).padStart(2, '0')
  const toISO = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  return { start: toISO(start), end: toISO(end) }
}

// Approximate period length in whole months, for the "N-month period" label
// shown to the preparer — purely informational, the backend does its own
// measurement independently rather than trusting this string.
export function periodLengthMonths(startISO, endISO) {
  if (!startISO || !endISO) return null
  const start = new Date(startISO), end = new Date(endISO)
  if (!(end > start)) return null
  const days = (end - start) / (1000 * 60 * 60 * 24)
  return Math.round(days / 30.4375)
}

const IFRS_CATEGORIES = [
  'Property Plant & Equipment','Accumulated Depreciation','Intangible Assets',
  'Investments','Deferred Tax Asset','Inventories','Trade and Other Receivables','Tax Receivables',
  'Cash and Cash Equivalents','Stated Capital','Retained Earnings',
  'Distributions to Beneficiaries',
  'Loans from Shareholders','Borrowings - Non-Current','Borrowings - Current',
  'Deferred Tax Liability','Trade and Other Payables','Deferred Revenue',
  'Revenue','Other Income','Cost of Sales','Employee Costs','Occupancy Costs',
  'Communication Expenses','Travel and Motor Expenses','General and Admin Expenses',
  'Professional Fees','Depreciation and Amortisation','Credit Losses',
  'Sales and Marketing','Finance Costs','Income Tax Expense',
]


// An account counts as "unmapped" if it has no mapping at all, no category,
// is explicitly flagged 'unassigned', has zero confidence (including the
// fallback object runMapping() writes when the mapping API call errors out —
// previously this slipped through silently and the account vanished from
// every total with no warning), or looks like a trial-balance total/
// difference/suspense row (see accountMapping.js — 8 Sep 2026 control-row
// fix). Delegates to the shared helper so this page can't drift out of sync
// with Review/Statements/Notes or the backend on what counts as unmapped;
// `name` is optional and defaults to undefined for existing single-arg
// callers, which is a no-op for the control-row check.
export const isUnmapped = (m, name) => isUnmappedAccount(m, name)

// BUG FIX (Sep 2026) — silent trial-balance-progress autosave failure.
// getOrCreateDraftJob() (api/kailux.js) already does .then((r) => r.data),
// so it resolves to the plain { afs_job_id } response body, NOT an
// axios-response-shaped object. proceedToDocumentsInner() used to do
// `const { data } = await getOrCreateDraftJob(...); afsJobId = data?.afs_job_id`
// — reaching for a `.data` property one level too deep, which is always
// undefined. That made afsJobId always null, which made the
// `if (afsJobId) { await saveJobProgress(...) }` branch below it dead code:
// the network call to get-or-create genuinely succeeded and created/found a
// real row every time (confirmed via Railway HTTP logs), but save-progress
// was never even attempted, and every categorized trial balance was
// silently discarded with zero error surfaced anywhere. Extracted as its
// own pure function so the response-shape contract has a direct test,
// rather than being buried and unverified inside the async handler — see
// test_trial_balance_response_unwrap in TrialBalance.test.jsx.
// AdditionalDocuments.jsx's own getOrCreateDraftJob call (~line 1058)
// already destructures `{ afs_job_id }` directly and was never affected —
// this bug was specific to this one call site.
export const extractAfsJobId = (getOrCreateDraftJobResponse) =>
  getOrCreateDraftJobResponse?.afs_job_id || null

// Single-sourcing fix (25 Jul 2026): this used to be a local JS reimplementation
// of the backend's aggregate_figures_from_accounts() — same category sign-nature
// rules, maintained as a completely separate copy with nothing enforcing the two
// stayed in sync (flagged as a known architectural-drift risk in
// kailux-fixes-log.md §4.1, and the reason generate_all_notes was found trusting
// client-computed figures outright in main.py). Figures are now computed
// exclusively server-side via aggregateFigures() — see the debounced useEffect
// and the forced recompute in proceedToDocuments below.

const PRIOR_GROUPS = [
  {
    label: 'BALANCE SHEET — ASSETS',
    fields: [
      { key:'Property Plant & Equipment',    label:'Property, Plant & Equipment (cost)' },
      { key:'Accumulated Depreciation',      label:'Accumulated Depreciation' },
      { key:'Intangible Assets',             label:'Intangible Assets' },
      { key:'Investments',                   label:'Investments' },
      { key:'Deferred Tax Asset',            label:'Deferred Tax Asset' },
      { key:'Inventories',                   label:'Inventories' },
      { key:'Trade and Other Receivables',   label:'Trade and Other Receivables' },
      { key:'Tax Receivables',               label:'Tax Receivables' },
      { key:'Cash and Cash Equivalents',     label:'Cash and Cash Equivalents' },
    ]
  },
  {
    label: 'BALANCE SHEET — EQUITY & LIABILITIES',
    fields: [
      { key:'Stated Capital',                label:"Stated Capital / Members' Contributions" },
      { key:'Retained Earnings',             label:'Retained Earnings (opening of prior year)' },
      { key:'Loans from Shareholders',       label:'Loans from Shareholders' },
      { key:'Borrowings - Non-Current',      label:'Borrowings — Non-Current' },
      { key:'Borrowings - Current',          label:'Borrowings — Current Portion' },
      { key:'Deferred Tax Liability',        label:'Deferred Tax Liability' },
      { key:'Trade and Other Payables',      label:'Trade and Other Payables' },
      { key:'Deferred Revenue',              label:'Deferred Revenue' },
    ]
  },
  {
    label: 'INCOME STATEMENT',
    fields: [
      { key:'Revenue',                       label:'Revenue / Turnover' },
      { key:'Cost of Sales',                 label:'Cost of Sales' },
      { key:'Other Income',                  label:'Other Income' },
      { key:'Employee Costs',                label:'Employee Costs' },
      { key:'Occupancy Costs',               label:'Occupancy / Rent' },
      { key:'Communication Expenses',        label:'Communication Expenses' },
      { key:'Travel and Motor Expenses',     label:'Travel and Motor Expenses' },
      { key:'General and Admin Expenses',    label:'General and Admin Expenses' },
      { key:'Professional Fees',             label:'Professional Fees' },
      { key:'Depreciation and Amortisation', label:'Depreciation and Amortisation' },
      { key:'Finance Costs',                 label:'Finance Costs / Interest Paid' },
      { key:'Income Tax Expense',            label:'Income Tax Expense' },
    ]
  },
]

const EMPTY_PRIOR = Object.fromEntries(
  PRIOR_GROUPS.flatMap(g => g.fields.map(f => [f.key, '']))
)

export default function TrialBalance() {
  const [screen,       setScreen]       = useState('upload')
  const [accounts,     setAccounts]     = useState([])
  const [mappings,     setMappings]     = useState({})
  // Single-sourcing fix (25 Jul 2026) — figures are now computed by the
  // backend (aggregate_figures_from_accounts, the same function used
  // authoritatively for real generation), not by a local JS mirror of that
  // logic. Debounced so rapid mapping edits on the review screen don't fire
  // a request per keystroke; the previous computed value is kept on screen
  // while a new one is in flight so the totals never blank out mid-edit.
  const [computedFigures,  setComputedFigures]  = useState({})
  const [unmappedAccounts, setUnmappedAccounts] = useState([])
  const [figuresPending,   setFiguresPending]   = useState(false)
  const figuresDebounceRef = useRef(null)
  useEffect(() => {
    if (accounts.length === 0) { setComputedFigures({}); setUnmappedAccounts([]); return }
    setFiguresPending(true)
    clearTimeout(figuresDebounceRef.current)
    figuresDebounceRef.current = setTimeout(() => {
      aggregateFigures(accounts, mappings)
        .then(({ data }) => {
          setComputedFigures(data.figures || {})
          setUnmappedAccounts(data.unmapped || [])
        })
        .catch(() => { /* keep last-known totals on screen; not fatal to editing */ })
        .finally(() => setFiguresPending(false))
    }, 400)
    return () => clearTimeout(figuresDebounceRef.current)
  }, [accounts, mappings])
  const [progress,     setProgress]     = useState(0)
  const [current,      setCurrent]      = useState('')
  const [search,       setSearch]       = useState('')
  const [mapError,     setMapError]     = useState('')
  const [editingCode,  setEditingCode]  = useState(null)
  const [uploading,    setUploading]    = useState(false)
  const [uploadError,  setUploadError]  = useState('')
  const [uploadedFile, setUploadedFile] = useState(null)
  const [dragOver,     setDragOver]     = useState(false)
  // Bug fix (Sep 2026) — see proceedToDocuments below: guards against the
  // "Review Notes ->" button (rendered twice, sticky header + footer, both
  // wired to the same handler) being triggered more than once for a single
  // intended click, which was racing two concurrent get-or-create calls.
  // The ref is the real, synchronous guard (see AdditionalDocuments.jsx's
  // identical proceed() fix for why useState alone isn't enough here —
  // two clicks in the same tick both read the same stale state value);
  // isAdvancing (state) only drives the button's visual disabled/label.
  const [isAdvancing,  setIsAdvancing]  = useState(false)
  const isAdvancingRef = useRef(false)

  // Firm settings
  const [autoMapping,   setAutoMapping]   = useState(true)
  const [mappingRow,    setMappingRow]    = useState(null) // code being mapped individually
  // 9 Sep 2026 — "Exclude" flow for a flagged control/suspense row (see
  // isControlRow). Deliberately separate from editingCode's normal mapping
  // edit — a control row is never really "mapped" (any category assigned to
  // it is silently ignored server-side by design), so it gets its own
  // narrow, audited path instead of pretending the category dropdown does
  // anything useful for it.
  const [excludingCode,     setExcludingCode]     = useState(null) // code currently in the exclude-review flow
  const [exclusionPreview,  setExclusionPreview]  = useState(null) // { balances_without, resulting_difference } | null
  const [exclusionChecking, setExclusionChecking] = useState(false)
  const [exclusionReason,   setExclusionReason]   = useState('')
  const [exclusionError,    setExclusionError]    = useState('')

  // Prior year state
  const [hasPriorJob,  setHasPriorJob]  = useState(null)
  const [priorJobData, setPriorJobData] = useState(null)  // the actual most-recent complete afs_jobs row, so we can pre-fill from it
  const [priorFigures, setPriorFigures] = useState(EMPTY_PRIOR)
  const [priorYear,    setPriorYear]    = useState('')
  const [firstYear,          setFirstYear]          = useState(false)
  const [priorNotAvailable,  setPriorNotAvailable]  = useState(false)
  const [priorChecked, setPriorChecked] = useState(false)

  const fileInputRef = useRef(null)
  const { firm, user } = useAuth()
  const navigate     = useNavigate()
  const location     = useLocation()
  const client       = location.state?.client

  // Financial year being prepared — user selects explicitly.
  // Phase 1 (Jul 2026): replaced the previous fixed 4-option year select with
  // a genuinely free date-range picker — no floor on how far back it goes,
  // no restriction to full 12-month periods, so a stub/interim engagement or
  // a historical back-year can be prepared without being forced into a
  // preset that doesn't match the actual engagement.
  const currentYear = new Date().getFullYear()
  const [periodStart, setPeriodStart] = useState(() => defaultPeriodDates(client, currentYear).start)
  const [periodEnd,   setPeriodEnd]   = useState(() => defaultPeriodDates(client, currentYear).end)
  const financialYear = periodEnd ? String(new Date(periodEnd).getFullYear()) : String(currentYear)

  // Phase 5 (Issue A) — rehydrate a resumed session. Previously Resume sent
  // the preparer to this page with the saved accounts/mappings sitting in
  // location.state, but nothing here ever read them — the page always
  // rendered its default blank 'upload' screen regardless, so the resumed
  // data existed in Supabase but was invisible on screen. This effect wires
  // it into the same state the normal upload flow populates, and jumps
  // straight to the mapping-confirmation screen (the resumed data already
  // reflects an uploaded, mapped TB — there's nothing to re-upload).
  //
  // period_start/period_end (not financial_year) are what actually drive
  // financialYear above post-Phase-1, so a resumed session must rehydrate
  // those — setting a non-existent setFinancialYear here would silently
  // leave the resumed page on today's default period instead of whatever
  // the preparer had actually selected.
  const resumedAfsJobIdRef = useRef(location.state?.afsJobId || null)
  useEffect(() => {
    const s = location.state
    if (!s?.accounts?.length) return
    setAccounts(s.accounts)
    setMappings(s.mappings || {})
    setFirstYear(!!s.first_year)
    setPriorNotAvailable(!!s.prior_not_available)
    if (s.period_start) setPeriodStart(s.period_start)
    if (s.period_end)   setPeriodEnd(s.period_end)
    setPriorChecked(true)
    setScreen('review')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load firm settings
  useEffect(() => {
    if (!firm?.id) return
    getFirm(firm.id)
      .then(({ data }) => {
        setAutoMapping(data.auto_mapping !== false)
      })
      .catch(() => {})
  }, [firm?.id])

  // Check prior AFS jobs for this client
  useEffect(() => {
    if (!firm?.id || !client?.id) return   // data not loaded yet — wait, do NOT skip the screen
    getAFSJobs(firm.id)
      .then(({ data }) => {
        const clientJobs = (data || [])
          .filter(j => j.client_id === client.id && j.status === 'complete')
          .sort((a, b) => Number(b.financial_year) - Number(a.financial_year))
        setHasPriorJob(clientJobs.length > 0)
        setPriorJobData(clientJobs[0] || null)
        // Always let the preparer make the choice explicitly — never auto-set
        // priorChecked here, even when a prior job exists. (This used to skip
        // the choice screen entirely on every repeat run for an existing
        // client, silently reusing the old job's figures with no preparer
        // input at all.) Storing the actual job row (not just a boolean) lets
        // "Use the prior AFS on file" pre-fill the comparative fields below
        // for review, instead of carrying figures forward with nothing shown.
      })
      .catch(() => {
        setHasPriorJob(false)  // API error — still show the screen, let user choose
        setPriorJobData(null)
      })
  }, [firm?.id, client?.id])

  // Warns if an AFS job already exists for this client + financial year before
  // proceeding into the prep flow (Phase 6, July 2026 batch, Issue A) — two
  // different team members could otherwise unknowingly duplicate the same
  // work with no visibility that one already started it. This is a soft
  // warning: the user can still continue (e.g. deliberately correcting a
  // prior AFS), it just isn't silent anymore.
  //
  // Returns true if it's fine to proceed (no duplicate, or user confirmed
  // anyway), false if the user backed out. Deliberately just the check, not
  // the navigate — proceedToDocuments below also needs draft persistence and
  // the Phase 1 period-date fields, so it calls this first rather than the
  // two proceed paths silently diverging.
  const checkNoDuplicateBeforeProceeding = async () => {
    try {
      const { data } = await checkDuplicateAFSJob(client.id, financialYear)
      if (data?.exists) {
        const who = data.created_by_email ? ` by ${data.created_by_email}` : ''
        const when = data.created_at ? ` on ${new Date(data.created_at).toLocaleDateString()}` : ''
        const proceed = window.confirm(
          `An AFS for ${data.client_name || client.name} for ${financialYear} already exists ` +
          `(status: ${data.status || 'unknown'}), prepared${who}${when}. Continue anyway?`
        )
        if (!proceed) return false
      }
    } catch {
      // Duplicate check failing shouldn't block a legitimate prep flow —
      // fail open and let the user proceed as before.
    }
    return true
  }

  // ── File handling ─────────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['xlsx','xls','csv'].includes(ext)) {
      setUploadError('Please upload an Excel (.xlsx) or CSV (.csv) file.')
      return
    }
    setUploadedFile(file); setUploadError(''); setUploading(true)
    try {
      const { data } = await parseTrialBalance(file)
      setAccounts(data.accounts)
    } catch(err) {
      const msg = err?.response?.data?.detail ||
        'Could not read this file. Please check it is a valid trial balance.'
      setUploadError(msg); setUploadedFile(null)
    } finally { setUploading(false) }
  }

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  // ── Prior year helpers ────────────────────────────────────────────────────
  const setPriorField = (key, val) => setPriorFigures(prev => ({ ...prev, [key]: val }))

  const priorFiguresForSubmit = () => {
    const out = {}
    Object.entries(priorFigures).forEach(([k, v]) => {
      const n = parseFloat(String(v).replace(/,/g, ''))
      if (!isNaN(n) && n !== 0) out[k] = n
    })
    return out
  }

  // Convert a stored completed job's `figures` (IFRS-category-keyed numbers,
  // the same shape as PRIOR_GROUPS' field keys) into the string-valued shape
  // the manual entry inputs use, so the preparer sees real numbers pre-filled
  // and can review/edit them before continuing — rather than the figures
  // being carried forward with nothing shown on screen.
  const populatePriorFromJob = (job) => {
    const src = job?.figures || {}
    const out = { ...EMPTY_PRIOR }
    Object.keys(out).forEach(key => {
      const v = src[key]
      if (v !== undefined && v !== null && v !== '') out[key] = String(v)
    })
    return out
  }

  const handleCategorizeClick = () => {
    if (hasPriorJob === null && !priorChecked) return  // still checking — button should be disabled but guard anyway
    if (!priorChecked) {
      setScreen('prior_year')  // always let the preparer choose, even when a prior job was found
    } else {
      if (autoMapping) {
        runMapping(accounts)
      } else {
        // Auto-mapping OFF — go straight to review with all unassigned
        const unassigned = {}
        accounts.forEach(acc => {
          unassigned[acc.code] = {
            ifrs_category: 'General and Admin Expenses',
            statement:     'Income Statement',
            confidence:    0,
            reasoning:     'Not yet mapped — click AI or select manually',
            display_name:  acc.name,
            source:        'unassigned',
          }
        })
        setMappings(unassigned)
        setScreen('review')
      }
    }
  }

  // Preparer chose to use the prior AFS job KaiLux already found on file for
  // this client. Pre-fill the comparative fields below from that job's real
  // closing figures and let the preparer review/edit them here — they still
  // click "Save & Continue" (handlePriorYearDone) themselves to proceed.
  // Deliberately does NOT set priorChecked or advance the screen: doing that
  // would carry the figures forward with nothing shown, which is the exact
  // behaviour this is fixing.
  const handleUsePriorJob = () => {
    setFirstYear(false)
    setPriorNotAvailable(false)
    if (priorJobData) {
      setPriorFigures(populatePriorFromJob(priorJobData))
      const y = priorJobData.financial_year
      setPriorYear(y ? `${client?.year_end_day || 28} ${client?.year_end_month || 'February'} ${y}` : '')
    }
  }

  const handlePriorYearDone = () => {
    setPriorChecked(true)
    if (autoMapping) {
      runMapping(accounts)
    } else {
      const unassigned = {}
      accounts.forEach(acc => {
        unassigned[acc.code] = {
          ifrs_category: 'General and Admin Expenses',
          statement:     'Income Statement',
          confidence:    0,
          reasoning:     'Not yet mapped — click AI or select manually',
          display_name:  acc.name,
          source:        'unassigned',
        }
      })
      setMappings(unassigned)
      setScreen('review')
    }
  }

  const handleFirstYearConfirm = () => {
    setFirstYear(true)
    setPriorNotAvailable(false)
    setPriorFigures(EMPTY_PRIOR)
    setPriorChecked(true)
    if (autoMapping) {
      runMapping(accounts)
    } else {
      const unassigned = {}
      accounts.forEach(acc => {
        unassigned[acc.code] = {
          ifrs_category: 'General and Admin Expenses',
          statement:     'Income Statement',
          confidence:    0,
          reasoning:     'Not yet mapped — click AI or select manually',
          display_name:  acc.name,
          source:        'unassigned',
        }
      })
      setMappings(unassigned)
      setScreen('review')
    }
  }

  // New: company exists but no prior AFS is available — no comparative column,
  // but opening retained earnings from the TB will be used (not zeroed out).
  const handlePriorNotAvailable = () => {
    setFirstYear(false)
    setPriorNotAvailable(true)
    setPriorFigures(EMPTY_PRIOR)
    setPriorChecked(true)
    if (autoMapping) {
      runMapping(accounts)
    } else {
      const unassigned = {}
      accounts.forEach(acc => {
        unassigned[acc.code] = {
          ifrs_category: 'General and Admin Expenses',
          statement:     'Income Statement',
          confidence:    0,
          reasoning:     'Not yet mapped — click AI or select manually',
          display_name:  acc.name,
          source:        'unassigned',
        }
      })
      setMappings(unassigned)
      setScreen('review')
    }
  }

  // ── Full AI mapping loop ──────────────────────────────────────────────────
  const runMapping = async (accs, targetCodes = null) => {
    const toMap = targetCodes
      ? accs.filter(a => targetCodes.includes(a.code))
      : accs

    if (!targetCodes) {
      setScreen('mapping')
    }

    setMapError('')
    const results = { ...mappings }

    if (!firm?.id) {
      setMapError('Firm ID is missing. Please log out and log back in.')
      setScreen('upload'); return
    }

    for (let i = 0; i < toMap.length; i++) {
      const acc = toMap[i]
      setCurrent(acc.name)
      setProgress(Math.round((i / toMap.length) * 100))
      try {
        const { data } = await mapAccount({
          firm_id: firm.id, code: acc.code,
          name: acc.name, debit: acc.debit, credit: acc.credit,
        })
        results[acc.code] = data
      } catch(err) {
        results[acc.code] = {
          ifrs_category: 'General and Admin Expenses',
          statement:     'Income Statement',
          confidence:    0,
          reasoning:     `API error: ${err?.response?.data?.detail || err.message}`,
          display_name:  acc.name,
        }
        setMapError('Some accounts could not be mapped — review them below.')
      }
      setMappings({ ...results })
    }

    setProgress(100)
    setMappingRow(null)
    if (!targetCodes) setScreen('review')
  }

  // ── Map single account ────────────────────────────────────────────────────
  const mapSingleAccount = async (code) => {
    setMappingRow(code)
    const acc = accounts.find(a => a.code === code)
    if (!acc) { setMappingRow(null); return }
    try {
      const { data } = await mapAccount({
        firm_id: firm.id, code: acc.code,
        name: acc.name, debit: acc.debit, credit: acc.credit,
      })
      setMappings(prev => ({ ...prev, [code]: data }))
    } catch(err) {
      setMapError(`Could not map "${acc.name}" — please select manually.`)
    } finally {
      setMappingRow(null)
    }
  }

  // ── Map all unassigned ────────────────────────────────────────────────────
  const mapAllUnassigned = () => {
    const unassignedCodes = accounts
      .filter(a => !mappings[a.code] || mappings[a.code]?.source === 'unassigned')
      .map(a => a.code)
    if (unassignedCodes.length === 0) return
    runMapping(accounts, unassignedCodes)
  }

  const editMapping = (code, field, value) => {
    // applyMappingEdit re-derives the statement/label when the category changes (see utils/accountMapping.js)
    setMappings(prev => ({ ...prev, [code]: applyMappingEdit(prev[code], field, value) }))
  }

  // Phase 4 — input-side anomaly detection: a low-confidence AI suggestion
  // (source !== 'manual', confidence < 70) previously just sat there
  // colour-coded red, with nothing stopping a preparer from never actually
  // looking at it before generating. This records an explicit human
  // decision — "yes, I looked at this one and it's right" — without
  // silently re-labelling it as if the preparer had chosen the category
  // themselves (unlike editMapping, this doesn't touch confidence/source).
  const confirmMapping = (code) => {
    setMappings(prev => ({
      ...prev,
      [code]: { ...prev[code], confirmed: true },
    }))
  }

  const confColor = (c) => c >= 90 ? T.gn : c >= 70 ? T.yl : T.rd

  // 9 Sep 2026 — control-row exclusion flow (see isControlRow). Opens the
  // review panel and asks the backend whether removing this row would
  // leave the trial balance in balance — the real gate, re-verified
  // server-side again at validate/generate time regardless of what this
  // preview says, so a stale check can never be exploited.
  const startExclusionReview = async (code) => {
    setExcludingCode(code); setExclusionPreview(null)
    setExclusionReason(''); setExclusionError(''); setExclusionChecking(true)
    try {
      const { data } = await checkTrialBalanceExclusion(accounts, code, mappings)
      setExclusionPreview(data)
    } catch (err) {
      setExclusionError(err?.response?.data?.detail ||
        'Could not check this account. Please try again.')
    } finally { setExclusionChecking(false) }
  }

  const cancelExclusionReview = () => {
    setExcludingCode(null); setExclusionPreview(null)
    setExclusionReason(''); setExclusionError('')
  }

  // Only reachable when exclusionPreview.balances_without is true — the UI
  // never renders a confirm action otherwise (see the row rendering below).
  // A mandatory reason is required so the audit trail (surfaced in the
  // Account Mapping validation panel via /api/afs/validate's
  // "excluded_control_rows" check) always explains why, not just who/when.
  const confirmExclusion = (code) => {
    if (!exclusionReason.trim()) return
    setMappings(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        excluded: true,
        excluded_by: user?.email || 'unknown',
        excluded_at: new Date().toISOString(),
        excluded_reason: exclusionReason.trim(),
      },
    }))
    cancelExclusionReview()
  }

  const filtered = accounts.filter(a =>
    search === '' ||
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.code.includes(search)
  )

  // ══════════════════════════════════════════════════════════════════════════
  // UPLOAD SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'upload') return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif", color:T.tx }}>
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv"
        style={{ display:'none' }} onChange={e => { const f = e.target.files[0]; if(f) handleFile(f) }}/>

      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`, padding:'0 32px',
        height:'62px', display:'flex', alignItems:'center', gap:'16px' }}>
        <BackButton onClick={() => navigate('/clients')} label="Clients"
          textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800' }}>
          Kai<span style={{color:T.or}}>Lux</span>
        </div>
        {client && <span style={{ fontSize:'14px', color:T.mt }}>/ {client.name}</span>}
        {!autoMapping && (
          <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'6px',
            background:alpha(T.tx, 0.06), border:`1px solid ${T.bd}`,
            color:T.mt, marginLeft:'auto' }}>
            Auto-mapping OFF — manual mode
          </span>
        )}
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'60px 32px' }}>
        <div style={{ maxWidth:'560px', width:'100%', textAlign:'center' }}>
          <div style={{ fontSize:'12px', letterSpacing:'4px', color:T.or,
            fontWeight:'700', marginBottom:'16px' }}>TRIAL BALANCE UPLOAD</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'36px',
            fontWeight:'800', letterSpacing:'-1px', marginBottom:'14px' }}>
            Upload your trial balance
          </div>
          <div style={{ fontSize:'15px', fontWeight:'400', color:T.lt, lineHeight:'1.7', marginBottom:'40px' }}>
            Excel or CSV from QuickBooks, Xero, Sage, Pastel, or manually prepared.
            KaiLux reads any format automatically.
          </div>

          {uploadError && (
            <div style={{ background:alpha(T.rd, 0.1), border:`1px solid ${T.rd}`,
              borderRadius:'8px', padding:'14px 18px', marginBottom:'16px',
              fontSize:'14px', color:T.rd, textAlign:'left' }}>
              <Icon name="alert" /> {uploadError}
            </div>
          )}

          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            style={{ border:`2px dashed ${dragOver ? T.or : uploadedFile ? T.gn : T.bd}`,
              borderRadius:'16px', padding:'48px 32px',
              background: dragOver ? T.ord : uploadedFile ? T.gnd : T.s1,
              marginBottom:'16px', cursor: uploading ? 'not-allowed' : 'pointer',
              transition:'all 0.2s' }}>
            {uploading ? (
              <>
                <div style={{ fontSize:'32px', marginBottom:'12px' }}><Icon name="hourglass" strokeWidth={1.5} style={{ color: T.mt }} /></div>
                <div style={{ fontSize:'15px', fontWeight:'700', marginBottom:'6px' }}>Reading your file...</div>
                <div style={{ fontSize:'13px', color:T.mt }}>KaiLux is identifying the column structure</div>
              </>
            ) : uploadedFile ? (
              <>
                <div style={{ fontSize:'32px', marginBottom:'12px' }}><Icon name="check" strokeWidth={1.5} style={{ color: T.gn }} /></div>
                <div style={{ fontSize:'15px', fontWeight:'700', marginBottom:'4px', color:T.gn }}>{uploadedFile.name}</div>
                <div style={{ fontSize:'13px', color:T.mt }}>Click to upload a different file</div>
              </>
            ) : (
              <>
                <div style={{ marginBottom:'12px' }}><BarChart3 size={30} strokeWidth={1.5} color={T.mt}/></div>
                <div style={{ fontSize:'15px', fontWeight:'700', marginBottom:'6px' }}>Drop your file here or click to browse</div>
                <div style={{ fontSize:'13px', color:T.mt, marginBottom:'16px' }}>.xlsx or .csv · Sage, QuickBooks, Xero, Pastel, or manual</div>
                <div style={{ display:'inline-block', padding:'9px 20px', borderRadius:'8px',
                  border:`1px solid ${T.bd}`, fontSize:'13px', color:T.mt }}>Browse files</div>
              </>
            )}
          </div>


                {accounts.length > 0 && (
                  <div style={{ marginBottom:'12px', display:'flex', flexDirection:'column', gap:'8px',
                    background:alpha(T.or, 0.06), border:`1px solid ${alpha(T.or, 0.2)}`,
                    borderRadius:'8px', padding:'12px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'13px', color:T.mt, fontWeight:'600', whiteSpace:'nowrap' }}>
                        <Icon name="calendar" /> Financial period being prepared:
                      </span>
                      <span style={{ fontSize:'12px', color:T.lt }}>from</span>
                      <input
                        type="date"
                        value={periodStart}
                        max={periodEnd || undefined}
                        onChange={e => setPeriodStart(e.target.value)}
                        style={{ padding:'6px 10px', background:T.s3, border:`1px solid ${T.bd}`,
                          borderRadius:'8px', color:T.tx, fontSize:'13px', fontWeight:'600',
                          fontFamily:"'Outfit',sans-serif", colorScheme:'dark' }}
                      />
                      <span style={{ fontSize:'12px', color:T.lt }}>to</span>
                      <input
                        type="date"
                        value={periodEnd}
                        min={periodStart || undefined}
                        onChange={e => setPeriodEnd(e.target.value)}
                        style={{ padding:'6px 10px', background:T.s3, border:`1px solid ${T.bd}`,
                          borderRadius:'8px', color:T.tx, fontSize:'13px', fontWeight:'600',
                          fontFamily:"'Outfit',sans-serif", colorScheme:'dark' }}
                      />
                    </div>
                    <div style={{ fontSize:'11px', color:T.mt }}>
                      {(() => {
                        const months = periodLengthMonths(periodStart, periodEnd)
                        if (months === null) return 'Pick a start and end date for this AFS — any range is fine, including a stub period or a historical year.'
                        if (months < 11 || months > 13) return <><Icon name="alert" /> {`${months}-month period — not a standard 12-month year. This is fine for a stub/interim or historical engagement; the AFS will say "period" rather than "year" throughout.`}</>
                        return `Standard ${months}-month period.`
                      })()}
                    </div>
                  </div>
                )}
          {accounts.length > 0 && (
            <div style={{ background:T.gnd, border:`1px solid ${alpha(T.gn, 0.2)}`,
              borderRadius:'12px', padding:'16px 20px', display:'flex',
              alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:T.gn }}>
                  <Icon name="check" /> {accounts.length} accounts loaded
                </div>
                <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                  {uploadedFile ? uploadedFile.name : 'Trial balance loaded'}
                  {autoMapping ? ' · AI will map automatically' : ' · Manual mapping mode'}
                </div>
              </div>
              <button
                onClick={handleCategorizeClick}
                disabled={hasPriorJob === null && !priorChecked}
                style={{
                  padding:'10px 22px', borderRadius:'8px', border:'none',
                  background: hasPriorJob === null && !priorChecked
                    ? T.mt
                    : T.prg,
                  color:T.onpri, fontFamily:"'Outfit',sans-serif",
                  fontSize:'14px', fontWeight:'700',
                  cursor: hasPriorJob === null && !priorChecked ? 'not-allowed' : 'pointer',
                  opacity: hasPriorJob === null && !priorChecked ? 0.6 : 1,
                  transition:'background 0.2s',
                }}>
                {hasPriorJob === null && !priorChecked
                  ? 'Checking prior records…'
                  : autoMapping ? 'Categorize with AI →' : 'Review Accounts →'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // ══════════════════════════════════════════════════════════════════════════
  // PRIOR YEAR SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'prior_year') return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif", color:T.tx }}>
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`, padding:'0 32px',
        height:'62px', display:'flex', alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <BackButton onClick={() => setScreen('upload')} label="Back"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          {client && <span style={{ fontSize:'14px', color:T.mt }}>/ {client.name}</span>}
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={handlePriorYearDone}
            style={{ padding:'7px 18px', borderRadius:'6px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
            Save & Continue →
          </button>
        </div>
      </div>

      <div style={{ maxWidth:'700px', margin:'0 auto', padding:'36px 24px' }}>
        <div style={{ marginBottom:'28px' }}>
          <div style={{ fontSize:'12px', letterSpacing:'3px', color:T.or, fontWeight:'700', marginBottom:'10px' }}>PRIOR YEAR COMPARATIVE FIGURES</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'800', letterSpacing:'-0.5px', marginBottom:'10px' }}>
            {hasPriorJob ? `Prior AFS found for ${client?.name}` : `No prior AFS found for ${client?.name}`}
          </div>
          <div style={{ fontSize:'14px', fontWeight:'400', color:T.lt, lineHeight:'1.7' }}>
            Choose how to handle the prior year column before we generate the AFS.
          </div>
        </div>

        {/* Three-option cards */}
        {[
          ...(hasPriorJob ? [{
            id: 'use_prior_job',
            icon: FileText,
            title: 'Use the prior AFS already on file',
            desc: 'KaiLux found a completed AFS for this client from an earlier financial year. This pre-fills the comparative figures below from its closing balances — review and edit them, then Save & Continue.',
            action: handleUsePriorJob,
          }] : []),
          {
            id: 'manual',
            icon: PenLine,
            title: 'Enter prior year figures manually',
            desc: 'You have a signed prior year AFS and want a full comparative column.',
            action: null,
          },
          {
            id: 'not_available',
            icon: FolderOpen,
            title: 'Prior year figures not available',
            desc: 'The company has traded before, but the prior year AFS is not to hand. No comparative column will be shown. The cover page will say "No comparative figures presented" — not "First year."',
            action: handlePriorNotAvailable,
          },
          {
            id: 'first_year',
            icon: Sparkles,
            title: 'First year of operation',
            desc: 'The company was incorporated this financial year and has no trading history. Opening equity will be zero.',
            action: handleFirstYearConfirm,
          },
        ].map(opt => (
          <div key={opt.id}
            onClick={opt.action || undefined}
            style={{
              background: T.s1, border: `1px solid ${T.bd}`, borderRadius:'12px',
              padding:'18px 20px', marginBottom:'12px',
              cursor: opt.action ? 'pointer' : 'default',
              display:'flex', alignItems:'flex-start', gap:'12px',
              transition:'border-color 0.15s',
            }}
            onMouseEnter={e => { if (opt.action) e.currentTarget.style.borderColor = T.or }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.bd }}>
            <opt.icon size={20} strokeWidth={1.75} style={{ flexShrink:0, marginTop:'2px', color:T.mt }} />
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:'700', fontSize:'14px', marginBottom:'4px' }}>{opt.title}</div>
              <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>{opt.desc}</div>
            </div>
            {opt.action && (
              <span style={{ color:T.or, fontSize:'18px', alignSelf:'center' }}>→</span>
            )}
          </div>
        ))}

        {/* AI hint: opening retained earnings auto-detected from TB */}
        {(() => {
          const openingRE = accounts
            .filter(a => {
              const n = a.name?.toLowerCase() || ''
              return n.includes('retained') || n.includes('opening balance') || n.includes('accumulated surplus')
            })
            .reduce((sum, a) => sum + Math.abs((a.credit || 0) - (a.debit || 0)), 0)
          return openingRE > 0 ? (
            <div style={{
              background:alpha(T.gn, 0.08), border:`1px solid ${alpha(T.gn, 0.25)}`,
              borderRadius:'8px', padding:'14px 18px', marginTop:'4px', marginBottom:'20px',
              fontSize:'14px', color:T.gn, lineHeight:'1.6',
            }}>
              <strong><Icon name="tip" /> KaiLux detected an opening retained earnings balance of R{openingRE.toLocaleString()} in your TB.</strong><br/>
              If you select <em>"Prior year figures not available"</em>, this amount will be used as the opening retained earnings — so your equity will roll forward correctly even without a comparative column.
            </div>
          ) : null
        })()}

        {/* Confirmation once figures have been pre-filled from a prior KaiLux job */}
        {priorJobData && !firstYear && !priorNotAvailable &&
          Object.values(priorFigures).some(v => v !== '') && (
          <div style={{
            background:alpha(T.gn, 0.08), border:`1px solid ${alpha(T.gn, 0.25)}`,
            borderRadius:'8px', padding:'14px 18px', marginTop:'4px', marginBottom:'20px',
            fontSize:'14px', color:T.gn, lineHeight:'1.6',
          }}>
            <strong><Icon name="check" /> Figures pre-filled from the prior AFS on file (FY {priorJobData.financial_year}).</strong><br/>
            Review and adjust any values below, then click Save & Continue.
          </div>
        )}

        {/* Manual entry form (only shown when not choosing a quick option) */}
        <div style={{ marginBottom:'24px' }}>
          <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
            color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
            PRIOR YEAR ENDED (e.g. 28 February 2024)
          </label>
          <input value={priorYear} onChange={e => setPriorYear(e.target.value)}
            placeholder="e.g. 28 February 2024"
            style={{ width:'280px', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
              borderRadius:'8px', color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
              outline:'none', boxSizing:'border-box' }}
            onFocus={e => e.target.style.borderColor = T.or}
            onBlur={e  => e.target.style.borderColor = T.bd}/>
        </div>

        {PRIOR_GROUPS.map((group, gi) => (
          <div key={gi} style={{ marginBottom:'28px' }}>
            <div style={{ fontSize:'10px', fontWeight:'700', color:T.or, letterSpacing:'2px',
              marginBottom:'14px', borderBottom:`1px solid ${T.bd}`, paddingBottom:'8px' }}>
              {group.label}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
              {group.fields.map(field => (
                <div key={field.key}>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'600',
                    color:T.lt, letterSpacing:'0.5px', marginBottom:'4px' }}>
                    {field.label}
                  </label>
                  <div style={{ position:'relative' }}>
                    <span style={{ position:'absolute', left:'10px', top:'50%',
                      transform:'translateY(-50%)', fontSize:'13px', color:T.mt }}>R</span>
                    <input type="number" value={priorFigures[field.key]}
                      onChange={e => setPriorField(field.key, e.target.value)}
                      placeholder="0"
                      style={{ width:'100%', padding:'9px 10px 9px 24px', background:T.s3,
                        border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                        fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                        outline:'none', boxSizing:'border-box' }}
                      onFocus={e => e.target.style.borderColor = T.or}
                      onBlur={e  => e.target.style.borderColor = T.bd}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display:'flex', gap:'12px', justifyContent:'flex-end',
          paddingTop:'16px', borderTop:`1px solid ${T.bd}` }}>
          <button onClick={handlePriorYearDone}
            style={{ padding:'11px 28px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
            Save & Continue →
          </button>
        </div>
      </div>
    </div>
  )

  // ══════════════════════════════════════════════════════════════════════════
  // MAPPING SCREEN (full auto-map progress)
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'mapping') return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, display:'flex', alignItems:'center', justifyContent:'center', padding:'40px' }}>
      <div style={{ maxWidth:'520px', width:'100%', textAlign:'center' }}>
        <div style={{ width:'64px', height:'64px', background:T.prg,
          borderRadius:'16px', margin:'0 auto 24px', display:'flex',
          alignItems:'center', justifyContent:'center' }}><Sparkles size={26} strokeWidth={1.5} color={T.onpri}/></div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'24px', fontWeight:'800', marginBottom:'8px' }}>AI Mapping in Progress</div>
        <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'32px' }}>
          KaiLux is mapping every account to IFRS for SMEs structure
        </div>
        <div style={{ background:T.s1, borderRadius:'999px', height:'8px',
          marginBottom:'12px', overflow:'hidden', border:`1px solid ${T.bd}` }}>
          <div style={{ height:'100%', borderRadius:'999px',
            background:T.or,
            width:`${progress}%`, transition:'width 0.4s ease' }}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', color:T.mt, marginBottom:'20px' }}>
          <span>{Object.keys(mappings).length} of {accounts.length} accounts</span>
          <span style={{ color:T.or, fontWeight:'700' }}>{progress}%</span>
        </div>
        <div style={{ background:T.s1, borderRadius:'8px', padding:'12px 16px',
          border:`1px solid ${T.bd}`, fontSize:'13px', color:T.mt,
          fontFamily:"'DM Mono',monospace", textAlign:'left' }}>
          <span style={{ color:T.or }}>▶ </span>{current || 'Initialising...'}
        </div>
        {mapError && (
          <div style={{ marginTop:'16px', background:alpha(T.rd, 0.1),
            border:`1px solid ${T.rd}`, borderRadius:'8px', padding:'12px 16px',
            fontSize:'13px', color:T.rd, textAlign:'left' }}>
            <Icon name="alert" /> {mapError}
          </div>
        )}
      </div>
    </div>
  )

  // ══════════════════════════════════════════════════════════════════════════
  // REVIEW SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  const done     = Object.keys(mappings).length
  const high     = Object.values(mappings).filter(m => m.confidence >= 90).length
  const mid      = Object.values(mappings).filter(m => m.confidence >= 70 && m.confidence < 90).length
  const low      = Object.values(mappings).filter(m => m.confidence < 70 && m.source !== 'unassigned').length
  const manual   = Object.values(mappings).filter(m => m.source === 'manual').length
  // 29 Sep 2026 — a "Current Year Earnings" row that duplicates the income-
  // statement accounts blocks like an unresolved control row until excluded
  // (see isProfitRollupRow / backend _is_profit_rollup_row).
  const rollupCodes = findProfitRollupCodes(accounts, mappings)
  const unassigned = accounts.filter(acc =>
    isUnmapped(mappings[acc.code], acc.name) ||
    (rollupCodes.has(String(acc.code)) && !mappings[acc.code]?.excluded)).length
  const unconfirmedLow = Object.values(mappings)
    .filter(m => m.source !== 'manual' && m.source !== 'unassigned' && m.confidence < 70 && !m.confirmed).length
  // computedFigures / unmappedAccounts now come from the debounced backend
  // effect above — see the single-sourcing note next to that useEffect.

  // Phase 5 (Issue A) — resolve a real afs_job_id and persist this step's
  // state before moving on, so the job is actually resumable if the
  // preparer stops here instead of continuing straight through to
  // Additional Documents. Best-effort: a save hiccup should never block
  // the preparer from proceeding right now, it only means the "resume from
  // exactly here" convenience wouldn't be available if they leave.
  const proceedToDocuments = async () => {
    if (unassigned > 0) return
    // Re-entrancy guard — this handler is bound to two separate buttons
    // (sticky header + footer, same "Review Notes ->" action) and does
    // several sequential awaits before navigating. Without this, a second
    // click (impatient double-click, or the two buttons both firing for
    // one intended click) starts a second, fully independent run of this
    // whole function concurrently — each doing its own get-or-create call
    // — before the first run has navigated away. Confirmed happening in
    // production logs: two /api/afs-jobs/get-or-create requests ~1.3s
    // apart from a single click-through.
    //
    // The ref is the actual guard (updates synchronously, so a second
    // click in the same tick sees it immediately); isAdvancing (state)
    // only drives the button's visual disabled/label — two clicks fired
    // back-to-back both run before React re-renders with updated state,
    // so a useState-only guard would let both through.
    if (isAdvancingRef.current) return
    isAdvancingRef.current = true
    setIsAdvancing(true)
    try {
      await proceedToDocumentsInner()
    } finally {
      isAdvancingRef.current = false
      setIsAdvancing(false)
    }
  }

  const proceedToDocumentsInner = async () => {
    // Single-sourcing fix: computedFigures is debounced (400ms) for the live
    // preview, so a mapping edit made just before clicking through could
    // otherwise submit stale figures. Force a fresh, synchronous-to-this-
    // call recompute right before anything gets persisted or navigated
    // forward with — this is the actual submission point, not just a
    // display, so it must reflect the current accounts/mappings exactly.
    let latestFigures = computedFigures
    try {
      const { data } = await aggregateFigures(accounts, mappings)
      latestFigures = data.figures || {}
      setComputedFigures(latestFigures)
      setUnmappedAccounts(data.unmapped || [])
    } catch {
      // Network hiccup — fall back to the last debounced value rather than
      // blocking the preparer entirely; the account list hasn't changed
      // since the last successful debounce in the overwhelming majority of
      // cases (this only matters for edits inside the last ~400ms).
    }
    if (firstYear) {
      const reBalance = accounts
        .filter(acc => (mappings[acc.code]?.ifrs_category) === 'Retained Earnings')
        .reduce((s, acc) => s + Math.abs((acc.credit || 0) - (acc.debit || 0)), 0)
      if (reBalance > 10) {
        const proceed = window.confirm(
          `This trial balance has a Retained Earnings opening balance of ` +
          `R${reBalance.toLocaleString()}, which means this is not actually a ` +
          `first-year entity — there is prior-period history. If you continue, ` +
          `the system will disregard the "first year" flag so the opening balance ` +
          `rolls forward correctly. Continue?`
        )
        if (!proceed) return
      }
    }
    // Pre-flight (28 Oct 2026): show the preparer what the integrity checks already say about THIS trial balance now,
    // not after documents, notes and review. Advisory — the generation gate still enforces; fails open on any error.
    try {
      const { data: pre } = await validateAFS({
        accounts, mappings, figures: latestFigures,
        prior_figures: firstYear || priorNotAvailable ? {} : priorFiguresForSubmit(),
        first_year: firstYear, client_id: client?.id, financial_year: financialYear,
        year_end: isoToDisplayDate(periodEnd),
      })
      const issues = preflightIssues(pre?.checks)
      if (issues.length > 0 && !window.confirm(preflightMessage(issues))) return
    } catch { /* never block the preparer on the early check itself */ }
    // Phase 6 (Issue A) — warn if this client+year already has an AFS job
    // before proceeding, so two team members can't unknowingly duplicate
    // the same work. Checked here (not just in confirmNoDuplicateOrProceed's
    // old standalone path) so it applies regardless of entry point.
    if (!(await checkNoDuplicateBeforeProceeding())) return
    sessionStorage.setItem('kailux:financial_year', financialYear)
    sessionStorage.setItem('kailux:year_end', isoToDisplayDate(periodEnd))
    sessionStorage.setItem('kailux:period_start_display', isoToDisplayDate(periodStart))
    const priorFigures = firstYear || priorNotAvailable ? {} : priorFiguresForSubmit()
    let afsJobId = resumedAfsJobIdRef.current
    // Bug fix (Sep 2026): this used to fail completely silently — if
    // get-or-create didn't yield an afsJobId, or save-progress threw, the
    // preparer was sent on to Additional Documents with no indication that
    // their trial balance categorization wasn't actually persisted, so
    // closing the tab from there (or anywhere further down the wizard)
    // silently lost all of it with no resumable checkpoint. Track it
    // explicitly now so the next screen can tell the preparer.
    let autosaveFailed = false
    try {
      if (!afsJobId) {
        const res = await getOrCreateDraftJob(client?.id, firm?.id, financialYear)
        afsJobId = extractAfsJobId(res)
      }
      if (afsJobId) {
        await saveJobProgress(afsJobId, {
          stage: 'trial_balance', accounts, mappings, figures: latestFigures,
          prior_figures: priorFigures, first_year: firstYear,
          prior_not_available: priorNotAvailable,
          // Phase 1 (Jul 2026): explicit period from the free date-range
          // picker — without this, resuming a saved draft would silently
          // fall back to today's default period instead of what was chosen.
          period_start: periodStart, period_end: periodEnd,
        })
      } else {
        autosaveFailed = true
      }
    } catch (err) {
      autosaveFailed = true
      console.error('Autosave failed (non-fatal — preparer can still continue):', err)
    }
    navigate('/additional-documents', {
      state:{
        client, mappings, accounts, figures: latestFigures,
        prior_figures: priorFigures,
        first_year: firstYear,
        prior_not_available: priorNotAvailable,
        financial_year: financialYear,
        afsJobId,
        autosave_failed: autosaveFailed,
        // Phase 1 (Jul 2026): explicit period, forwarded so downstream steps
        // (generate, XBRL export) don't fall back to the fixed
        // year_end_month/day assumption.
        year_end: isoToDisplayDate(periodEnd),
        period_start: isoToDisplayDate(periodStart),
        period_start_iso: periodStart,
        period_end_iso: periodEnd,
      }
    })
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, paddingBottom:'60px' }}>

      {/* Nav */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:'0 28px', height:'62px', display:'flex', alignItems:'center',
        justifyContent:'space-between', position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
            background:T.ord, color:T.or, fontWeight:'700' }}>AI Mapping Review</span>
          {!firstYear && Object.keys(priorFiguresForSubmit()).length > 0 && (
            <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
              background:T.gnd, color:T.gn, fontWeight:'700' }}><Icon name="check" /> Prior year figures loaded</span>
          )}
          {firstYear && (
            <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
              background:alpha(T.tx, 0.05), color:T.mt, fontWeight:'700' }}>
              First year — no comparatives
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          {!autoMapping && unassigned > 0 && (
            <button onClick={mapAllUnassigned}
              style={{ padding:'7px 16px', borderRadius:'6px', border:'none',
                background:T.bl, color:T.onpri,
                fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
              <Icon name="ai" /> Map all unassigned ({unassigned}) →
            </button>
          )}
          <button onClick={() => { setScreen('upload'); setMappings({}); setMapError('') }}
            style={{ padding:'7px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
              background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
              fontSize:'13px', cursor:'pointer' }}>
            ← New Upload
          </button>
          <button
            disabled={unassigned > 0 || isAdvancing}
            title={unassigned > 0
              ? `${unassigned} account(s) are unmapped — they would be silently excluded from the financial statements. Map them first.`
              : ''}
            onClick={proceedToDocuments}
            style={{ padding:'7px 18px', borderRadius:'6px', border:'none',
              background: (unassigned > 0 || isAdvancing) ? T.dm : T.prg,
              color: (unassigned > 0 || isAdvancing) ? T.mt : T.onpri, cursor: (unassigned > 0 || isAdvancing) ? 'not-allowed' : 'pointer',
              fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700' }}>
            {isAdvancing ? 'Saving…' : 'Review Notes →'}
          </button>
        </div>
      </div>

      {unassigned > 0 && (
        <div style={{ margin:'16px 28px 0', background:alpha(T.rd, 0.1),
          border:`1px solid ${T.rd}`, borderRadius:'8px', padding:'12px 16px',
          fontSize:'13px', color:T.rd }}>
          <Icon name="alert" /> {unassigned} account{unassigned > 1 ? 's' : ''} unmapped — these will not appear
          anywhere in the financial statements until mapped: {unmappedAccounts.map(a => a.name).join(', ')}
        </div>
      )}

      <div style={{ padding:'24px 28px' }}>

        {mapError && (
          <div style={{ background:alpha(T.rd, 0.1), border:`1px solid ${T.rd}`,
            borderRadius:'8px', padding:'14px 18px', marginBottom:'16px',
            fontSize:'14px', color:T.rd }}>
            <Icon name="alert" /> {mapError}
          </div>
        )}

        {!autoMapping && (
          <div style={{ background:alpha(T.bl, 0.08), border:`1px solid ${alpha(T.bl, 0.2)}`,
            borderRadius:'8px', padding:'12px 16px', marginBottom:'16px',
            fontSize:'13px', color:T.bl }}>
            <Icon name="ai" /> <strong>Manual mapping mode.</strong> Use the <strong>AI</strong> button on each row
            to map individually, or <strong>Map all unassigned</strong> to run AI on everything at once.
            You can also select any category from the dropdown directly.
          </div>
        )}

        {autoMapping && (
          <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'8px',
            padding:'10px 16px', marginBottom:'16px', fontSize:'13px', color:T.mt,
            display:'flex', alignItems:'center', gap:'8px' }}>
            <span style={{ color:T.or }}><Icon name="pencil" /></span>
            Click any <strong style={{color:T.or}}>orange category badge</strong> to correct a mapping.
            {manual > 0 && <span style={{ marginLeft:'auto', color:T.gn, fontWeight:'700' }}>{manual} manually corrected</span>}
          </div>
        )}

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
          {[
            { label:'Accounts',      value:done,       color:T.tx },
            { label:'High Conf',     value:high,       color:T.gn },
            { label:'Need Review',   value:mid,        color:T.yl },
            { label: autoMapping ? 'Low Conf' : 'Unassigned', value: autoMapping ? low : unassigned, color:T.rd },
          ].map((s,i) => (
            <div key={i} style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'16px' }}>
              <div style={{ fontSize:'9px', color:T.mt, letterSpacing:'1px', marginBottom:'6px' }}>{s.label.toUpperCase()}</div>
              <div style={{ fontSize:'28px', fontWeight:'800', color:s.color, letterSpacing:'-1px' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ marginBottom:'12px' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search accounts..."
            style={{ padding:'9px 14px', background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'8px', color:T.tx, fontSize:'13px', fontFamily:"'Outfit',sans-serif",
              outline:'none', width:'220px' }}
            onFocus={e => e.target.style.borderColor = T.or}
            onBlur={e  => e.target.style.borderColor = T.bd}/>
        </div>

        {/* Table header */}
        <div style={{ display:'grid',
          gridTemplateColumns: autoMapping ? '80px 1fr 220px 200px 90px 80px 80px' : '80px 1fr 220px 200px 90px 80px 80px 80px',
          padding:'8px 14px', fontSize:'9px', letterSpacing:'1.5px',
          color:T.mt, fontWeight:'700', borderBottom:`1px solid ${T.bd}` }}>
          <div>CODE</div><div>ACCOUNT</div>
          <div>IFRS CATEGORY <span style={{color:T.or}}><Icon name="pencil" /></span></div>
          <div>STATEMENT <span style={{color:T.or}}><Icon name="pencil" /></span></div>
          <div style={{textAlign:'right'}}>DEBIT</div>
          <div style={{textAlign:'right'}}>CREDIT</div>
          <div style={{textAlign:'center'}}>CONF</div>
          {!autoMapping && <div style={{textAlign:'center'}}>AI</div>}
        </div>

        {filtered.map((acc, i) => {
          const m = mappings[acc.code]
          if (!m) return null
          const isEditing   = editingCode === acc.code
          const isMappingMe = mappingRow === acc.code
          const isUnassigned = m.source === 'unassigned'
          // 9 Sep 2026 — a control/suspense row (see isControlRow) is never
          // really "mapped" in the normal sense: whatever category is
          // picked for it is silently ignored server-side by design (see
          // aggregate_figures_from_accounts). So instead of letting the
          // preparer fiddle with a dropdown that does nothing, this row
          // gets its own distinct treatment and the narrow, audited
          // "Review & exclude" path.
          const isRollup      = rollupCodes.has(String(acc.code))
          const isCtrl        = isControlRow(acc.name) || isRollup
          const isExcludedRow = isCtrl && (isRollup ? !!m.excluded : isExcludedControlRow(m, acc.name))
          const isReviewingThis = excludingCode === acc.code
          const rowBg = isCtrl ? (isExcludedRow ? alpha(T.gn, 0.05) : alpha(T.bl, 0.05))
            : isUnassigned ? alpha(T.rd, 0.04)
            : isEditing ? alpha(T.or, 0.04)
            : i % 2 === 0 ? 'transparent' : alpha(T.tx, 0.01)

          return (
            <Fragment key={acc.code}>
            <div
              style={{ display:'grid',
                gridTemplateColumns: autoMapping ? '80px 1fr 220px 200px 90px 80px 80px' : '80px 1fr 220px 200px 90px 80px 80px 80px',
                padding:'10px 14px', borderBottom:`1px solid ${T.bd}`,
                alignItems:'center', transition:'background 0.15s',
                background: rowBg }}
              onMouseEnter={e => { if(!isEditing) e.currentTarget.style.background = T.s2 }}
              onMouseLeave={e => { if(!isEditing) e.currentTarget.style.background = rowBg }}>

              <div style={{ fontSize:'12px', color:T.mt, fontFamily:"'DM Mono',monospace" }}>{acc.code}</div>
              <div>
                <div style={{ fontSize:'13px' }}>{acc.name}</div>
                {m.source === 'manual' && !isCtrl && <div style={{ fontSize:'10px', color:T.gn }}><Icon name="check" /> Manually corrected</div>}
                {isUnassigned && !isCtrl && <div style={{ fontSize:'10px', color:T.rd }}><Icon name="alert" /> Not yet mapped</div>}
                {isCtrl && !isExcludedRow && (
                  <div style={{ fontSize:'10px', color:T.bl }}>
                    {isRollup
                      ? <><Icon name="flag" /> Duplicates your income-statement accounts — counting it would double-count the year's profit</>
                      : <><Icon name="flag" /> Looks like a trial balance total/difference/suspense row</>}
                  </div>
                )}
                {isCtrl && isExcludedRow && (
                  <div style={{ fontSize:'10px', color:T.gn }}>
                    <Icon name="check" /> Excluded by {m.excluded_by || 'unknown'} on{' '}
                    {m.excluded_at ? new Date(m.excluded_at).toLocaleDateString() : 'unknown date'} —
                    "{m.excluded_reason || 'no reason given'}"
                  </div>
                )}
              </div>
              <div>
                {isCtrl ? (
                  isExcludedRow ? (
                    <span style={{ fontSize:'10px', fontWeight:'700', padding:'3px 8px', borderRadius:'6px',
                      background:alpha(T.gn, 0.15), color:T.gn, display:'inline-block' }}>
                      Excluded
                    </span>
                  ) : (
                    <button onClick={() => startExclusionReview(acc.code)}
                      style={{ padding:'4px 10px', borderRadius:'6px', border:`1px solid ${T.bl}`,
                        background:'transparent', color:T.bl, fontSize:'11px', fontWeight:'700',
                        cursor:'pointer', whiteSpace:'nowrap' }}>
                      Review &amp; exclude…
                    </button>
                  )
                ) : isEditing ? (
                  <select autoFocus value={m.ifrs_category}
                    onChange={e => { editMapping(acc.code,'ifrs_category',e.target.value); setEditingCode(null) }}
                    onBlur={() => setEditingCode(null)}
                    style={{ width:'100%', padding:'5px 8px', background:T.s3,
                      border:`1px solid ${T.or}`, borderRadius:'6px', color:T.tx,
                      fontSize:'12px', fontFamily:"'Outfit',sans-serif", outline:'none' }}>
                    {IFRS_CATEGORIES.map(cat => (
                      <option key={cat} value={cat} style={{background:T.s2}}>{cat}</option>
                    ))}
                  </select>
                ) : (
                  <span onClick={() => setEditingCode(acc.code)} title="Click to edit"
                    style={{ fontSize:'10px', fontWeight:'700', padding:'3px 8px',
                      borderRadius:'6px',
                      background: isUnassigned ? alpha(T.rd, 0.15) : T.ord,
                      color: isUnassigned ? T.rd : T.or,
                      cursor:'pointer', display:'inline-block',
                      border:'1px solid transparent', transition:'border 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.border=`1px solid ${isUnassigned ? T.rd : T.or}`}
                    onMouseLeave={e => e.currentTarget.style.border='1px solid transparent'}>
                    {isUnassigned ? <>Unassigned <Icon name="pencil" /></> : <>{`${m.ifrs_category}`} <Icon name="pencil" /></>}
                  </span>
                )}
              </div>
              <div>
                {isCtrl ? (
                  <span style={{ fontSize:'12px', color:T.mt }}>—</span>
                ) : isEditing ? (
                  // The statement follows the category and is no longer separately editable.
                  <span style={{ fontSize:'12px', color:T.mt }}>{statementForCategory(m.ifrs_category)}</span>
                ) : (
                  <>
                    <span onClick={() => setEditingCode(acc.code)} title="Click to edit"
                      style={{ fontSize:'12px', color:T.mt, cursor:'pointer' }}>
                      {statementForCategory(m.ifrs_category)}
                    </span>
                    {hasStatementConflict(m) && (
                      <button onClick={() => editMapping(acc.code, 'ifrs_category', m.ifrs_category)}
                        title={`This account was saved as "${m.statement}" but is categorised "${m.ifrs_category}". ` +
                               `The category decides where the amount is reported. Click to confirm the category.`}
                        style={{ marginLeft:'8px', fontSize:'10px', fontWeight:'700', padding:'2px 7px', borderRadius:'6px',
                          cursor:'pointer', border:`1px solid ${T.or}`, background:'transparent', color:T.or }}>
                        Confirm category
                      </button>
                    )}
                  </>
                )}
              </div>
              <div style={{ textAlign:'right', fontSize:'13px', color: acc.debit > 0 ? T.tx : T.mt }}>
                {acc.debit > 0 ? `R ${acc.debit.toLocaleString()}` : '—'}
              </div>
              <div style={{ textAlign:'right', fontSize:'13px', color: acc.credit > 0 ? T.tx : T.mt }}>
                {acc.credit > 0 ? `R ${acc.credit.toLocaleString()}` : '—'}
              </div>
              <div style={{ textAlign:'center' }}>
                {isCtrl ? (
                  <span style={{ fontSize:'13px', fontWeight:'800', color: isExcludedRow ? T.gn : T.bl }}
                    title={isExcludedRow ? 'Excluded — see reason above' : 'Flagged — needs review'}>
                    {isExcludedRow ? <Icon name="check" /> : <Icon name="flag" />}
                  </span>
                ) : m.source==='manual' ? (
                  <span style={{ fontSize:'13px', fontWeight:'800', color:T.gn }}><Icon name="check" /></span>
                ) : isUnassigned ? (
                  <span style={{ fontSize:'13px', fontWeight:'800', color:T.rd }}>—</span>
                ) : (m.confidence < 70 && !m.confirmed) ? (
                  <button onClick={() => confirmMapping(acc.code)}
                    title="Review this low-confidence suggestion, then confirm it's correct"
                    style={{ padding:'2px 8px', borderRadius:'6px', border:`1px solid ${T.rd}`,
                      background:'transparent', color:T.rd, fontSize:'11px', fontWeight:'700',
                      cursor:'pointer', whiteSpace:'nowrap' }}>
                    Confirm {m.confidence}%
                  </button>
                ) : (m.confidence < 70 && m.confirmed) ? (
                  <span style={{ fontSize:'12px', fontWeight:'700', color:T.gn }}
                    title="Low-confidence suggestion, reviewed and confirmed">
                    <Icon name="check" /> {m.confidence}%
                  </span>
                ) : (
                  <span style={{ fontSize:'13px', fontWeight:'800', color:confColor(m.confidence) }}>
                    {m.confidence}%
                  </span>
                )}
              </div>
              {!autoMapping && (
                <div style={{ textAlign:'center' }}>
                  <button
                    onClick={() => mapSingleAccount(acc.code)}
                    disabled={isMappingMe || isCtrl}
                    title={isCtrl ? "Control/suspense rows aren't mapped with AI — use Review & exclude instead" : "Map this account with AI"}
                    style={{ padding:'4px 10px', borderRadius:'6px', border:'none',
                      background: isMappingMe ? alpha(T.bl, 0.3) : alpha(T.bl, 0.15),
                      color: isMappingMe ? T.bl : T.bl,
                      fontFamily:"'Outfit',sans-serif", fontSize:'10px',
                      fontWeight:'700', cursor: (isMappingMe || isCtrl) ? 'not-allowed' : 'pointer',
                      opacity: isCtrl ? 0.4 : 1,
                      transition:'all 0.2s' }}>
                    {isMappingMe ? '...' : <><Icon name="ai" /> AI</>}
                  </button>
                </div>
              )}
            </div>

            {isReviewingThis && (
              <div style={{ padding:'16px 20px', background:alpha(T.bl, 0.04),
                borderBottom:`1px solid ${T.bd}`, borderLeft:`3px solid ${T.bl}` }}>
                <div style={{ fontSize:'12px', fontWeight:'700', color:T.tx, marginBottom:'8px' }}>
                  Excluding "{acc.name}" (R {Math.max(acc.debit, acc.credit).toLocaleString()})
                </div>
                {exclusionChecking && (
                  <div style={{ fontSize:'12px', color:T.mt }}>
                    Checking whether your trial balance still balances without this row…
                  </div>
                )}
                {exclusionError && (
                  <div style={{ fontSize:'12px', color:T.rd, marginBottom:'8px' }}>{exclusionError}</div>
                )}
                {exclusionPreview && exclusionPreview.balances_without && (
                  <>
                    <div style={{ fontSize:'12px', color:T.gn, marginBottom:'10px' }}>
                      <Icon name="check" /> Your trial balance stays in balance without this row — it isn't plugging a real gap.
                    </div>
                    <textarea value={exclusionReason} onChange={e => setExclusionReason(e.target.value)}
                      placeholder="Why are you excluding this? (required — recorded on the job for your records)"
                      rows={2}
                      style={{ width:'100%', padding:'8px 10px', background:T.s3, border:`1px solid ${T.bd}`,
                        borderRadius:'6px', color:T.tx, fontSize:'12px', fontFamily:"'Outfit',sans-serif",
                        outline:'none', resize:'vertical', marginBottom:'10px' }}/>
                    <div style={{ display:'flex', gap:'8px' }}>
                      <button onClick={() => confirmExclusion(acc.code)} disabled={!exclusionReason.trim()}
                        style={{ padding:'6px 14px', borderRadius:'6px', border:'none',
                          background: T.gn, opacity: exclusionReason.trim() ? 1 : 0.4,
                          color:T.onpri, fontSize:'12px', fontWeight:'700',
                          cursor: exclusionReason.trim() ? 'pointer' : 'not-allowed' }}>
                        Confirm exclude
                      </button>
                      <button onClick={cancelExclusionReview}
                        style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background:'transparent', color:T.mt, fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </>
                )}
                {exclusionPreview && !exclusionPreview.balances_without && (
                  <>
                    <div style={{ fontSize:'12px', color:T.rd, marginBottom:'10px', fontWeight:'600' }}>
                      <Icon name="alert" /> Removing this account would leave your trial balance out of balance by
                      R {Math.abs(exclusionPreview.resulting_difference).toLocaleString()}. This may indicate a
                      genuine discrepancy in your accounting records — please resolve it in your books before
                      continuing. This account cannot be excluded here.
                    </div>
                    <button onClick={cancelExclusionReview}
                      style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color:T.mt, fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
                      Close
                    </button>
                  </>
                )}
              </div>
            )}
            </Fragment>
          )
        })}

        {/* Bottom CTA */}
        <div style={{ marginTop:'24px', padding:'20px 24px',
          background:T.ord, border:`1px solid ${alpha(T.or, 0.2)}`,
          borderRadius:'12px', display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <div style={{ fontSize:'15px', fontWeight:'700', marginBottom:'3px' }}>
              {done} accounts mapped · {high} high confidence
              {manual > 0 && ` · ${manual} manually corrected`}
              {unassigned > 0 && ` · ${unassigned} still unassigned`}
              {unconfirmedLow > 0 && ` · ${unconfirmedLow} low-confidence suggestion(s) need confirmation`}
            </div>
            <div style={{ fontSize:'13px', color:T.mt }}>
              {unassigned > 0
                ? `${unassigned} accounts need mapping before proceeding`
                : 'All mappings confirmed — ready to review notes'}
            </div>
          </div>
          <button
            disabled={unassigned > 0 || isAdvancing}
            title={unassigned > 0
              ? `${unassigned} account(s) are unmapped — they would be silently excluded from the financial statements. Map them first.`
              : ''}
            onClick={proceedToDocuments}
            style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
              background: (unassigned > 0 || isAdvancing) ? T.dm : T.prg,
              color: (unassigned > 0 || isAdvancing) ? T.mt : T.onpri, cursor: (unassigned > 0 || isAdvancing) ? 'not-allowed' : 'pointer',
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'800' }}>
            {isAdvancing ? 'Saving…' : 'Review Notes →'}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}


